@import 'tailwindcss';
@import 'tw-animate-css';

@import './fonts.css';
@import './scrollbar.css';

@custom-variant dark (&:is(.dark *));

/* DXC COLORS */
:root {
  /* Colors — Primary Palette */
  --dxc-canvas: #f6f3f0;
  --dxc-white: #ffffff;
  --dxc-midnight-blue: #0e1020;
  --dxc-royal: #004aac;
  --dxc-red: #d14600;
  --dxc-gold: #ffae41;
  --dxc-true-blue: #4995ff;

  /* Colors — Secondary Accents (Data Viz Only) */
  --dxc-sky: #a1e6ff;
  --dxc-peach: #ffc982;
  --dxc-melon: #ff7e51;

  /* Gradients */
  --dxc-gradient-primary: linear-gradient(90deg, #004aac 0%, #ffae41 50%, #4995ff 100%);
  --dxc-gradient-vertical: linear-gradient(180deg, #004aac 0%, #ffae41 50%, #4995ff 100%);

  --talent-primary: var(--dxc-true-blue);
}

/* TALENT IQ COLORS */
:root {
  --talent-surface-canvas: var(--dxc-canvas);
  --talent-surface-bg: #f7f3f1;
  --talent-surface-secondary: var(--dxc-white);
  --talent-surface-shadow: #d6e1e9;
  --talent-surface-patch: #f1f7ff;
  --talent-surface-stroke: #f0f0f0;
  --talent-chip-bg: #ffcbb9;

  --talent-main-button-inactive: #b9b9c8;
  --talent-main-button-active: #4a95ff;
  --talent-main-button-hover: #408bf5;

  --talent-text-main: #0e1020;
  --talent-text-secondary: #718096;
  --talent-text-button: #fff;

  --talent-blue-10: #e8f1ff;
  --talent-blue-20: #d1e3ff;
  --talent-blue-30: #b9d5ff;
  --talent-blue-40: #458ffd;
  --talent-blue-50: #1874ff;
  --talent-blue-60: #005be3;
  --talent-blue-70: #0246ad;
  --talent-blue-80: #043b8c;
  --talent-blue-90: #002762;

  --talent-chip-success-bg: #d7f7ed;
  --talent-chip-success-text: #0c8553;

  --talent-chip-neutral-bg: #eae3df;
  --talent-red-alert: #c81834;
  --talent-divider: rgba(0, 0, 0, 0.2);
}

.dark {
  --talent-surface-secondary: #2e2c43;
  --talent-surface-shadow: #000;
  --talent-surface-patch: #0e1020;
  --talent-surface-stroke: #4c5264;

  --talent-text-main: #fff;
  --talent-text-secondary: #b3bbc6;

  --talent-chip-success-bg: #065952;
  --talent-chip-success-text: #c0f5df;
}

:root {
  font-family:
    'Inter',
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    'Noto Sans',
    Ubuntu,
    Cantarell,
    Arial,
    sans-serif;
  --font-mono:
    'Roboto Mono', 'Ubuntu Mono', 'Cascadia Code', 'Fira Mono', 'Consolas', 'Courier New', monospace;
  line-height: 1.5;
  font-weight: 400;
  font-size: 1rem;

  /* SYSTEM VARS */
  --radius: 0.625rem;
  --background: oklch(1 0 0);
  --foreground: var(--talent-text-main);
  --card: var(--talent-surface-secondary);
  --card-foreground: oklch(0.145 0 0);
  --popover: oklch(1 0 0);
  --popover-foreground: oklch(0.145 0 0);
  --primary: var(--talent-primary);
  --primary-foreground: oklch(0.985 0 0);
  --secondary: oklch(0.97 0 0);
  --secondary-foreground: oklch(0.205 0 0);
  --muted: oklch(0.97 0 0);
  --muted-foreground: oklch(0.556 0 0);
  --accent: oklch(0.97 0 0);
  --accent-foreground: oklch(0.205 0 0);
  --destructive: var(--dxc-red);
  --border: var(--talent-surface-stroke);
  --input: var(--talent-surface-stroke);
  --ring: var(--talent-surface-stroke);

  --chart-1: var(--dxc-midnight-blue);
  --chart-2: var(--dxc-royal);
  --chart-3: var(--dxc-red);
  --chart-4: var(--dxc-gold);
  --chart-5: var(--dxc-true-blue);
  --chart-6: var(--dxc-sky);
  --chart-7: var(--dxc-peach);
  --chart-8: var(--dxc-melon);

  --sidebar: var(--talent-surface-secondary);
  --sidebar-foreground: var(--talent-text-main);
  --sidebar-primary: oklch(0.205 0 0);
  --sidebar-primary-foreground: var(--talent-text-main);
  --sidebar-accent: oklch(0.97 0 0);
  --sidebar-accent-foreground: oklch(0.205 0 0);
  --sidebar-border: oklch(0.922 0 0);
  --sidebar-ring: oklch(0.708 0 0);

  --warning: var(--dxc-gold);
}

.dark {
  --background: oklch(0.145 0 0);
  --foreground: var(--talent-text-main);
  --card: var(--talent-surface-secondary);
  --card-foreground: oklch(0.985 0 0);
  --popover: oklch(0.205 0 0);
  --popover-foreground: oklch(0.985 0 0);
  --primary: var(--talent-primary);
  --primary-foreground: oklch(0.985 0 0);
  --secondary: oklch(0.269 0 0);
  --secondary-foreground: oklch(0.985 0 0);
  --muted: oklch(0.269 0 0);
  --muted-foreground: oklch(0.708 0 0);
  --accent: oklch(0.269 0 0);
  --accent-foreground: oklch(0.985 0 0);
  --destructive: var(--dxc-red);
  --border: var(--talent-surface-stroke);
  --input: var(--talent-surface-stroke);
  --ring: var(--talent-surface-stroke);

  --sidebar: var(--talent-surface-secondary);
  --sidebar-foreground: var(--talent-text-main);
  --sidebar-primary: oklch(0.488 0.243 264.376);
  --sidebar-primary-foreground: var(--talent-text-main);
  --sidebar-accent: oklch(0.269 0 0);
  --sidebar-accent-foreground: oklch(0.985 0 0);
  --sidebar-border: oklch(1 0 0 / 10%);
  --sidebar-ring: oklch(0.556 0 0);
}

/* DXC */
@theme inline {
  /* Typography — Families */
  --font-headline: 'GT Standard Extended', sans-serif;
  --font-body: 'Inter', Arial, sans-serif;

  --color-talent-surface-patch: var(--talent-surface-patch);
  --color-talent-surface: var(--talent-surface-bg);
  --color-chip-surface: var(--talent-chip-bg);
  --color-chip-success-background: var(--talent-chip-success-bg);
  --color-chip-success-foreground: var(--talent-chip-success-text);
  --color-talent-chip-neutral: var(--talent-chip-neutral-bg);
  --color-talent-red-alert: var(--talent-red-alert);
  --color-talent-divider: var(--talent-divider);

  /* DXC palette — usable as Tailwind colors (e.g. bg-dxc-royal, from-dxc-melon) */
  --color-dxc-canvas: var(--dxc-canvas);
  --color-dxc-midnight-blue: var(--dxc-midnight-blue);
  --color-dxc-royal: var(--dxc-royal);
  --color-dxc-red: var(--dxc-red);
  --color-dxc-gold: var(--dxc-gold);
  --color-dxc-true-blue: var(--dxc-true-blue);
  --color-dxc-sky: var(--dxc-sky);
  --color-dxc-peach: var(--dxc-peach);
  --color-dxc-melon: var(--dxc-melon);
}

@theme inline {
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--talent-text-secondary);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);

  --color-chart-1: var(--chart-1);
  --color-chart-2: var(--chart-2);
  --color-chart-3: var(--chart-3);
  --color-chart-4: var(--chart-4);
  --color-chart-5: var(--chart-5);

  --color-sidebar: var(--sidebar);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-sidebar-primary: var(--sidebar-primary);
  --color-sidebar-primary-foreground: var(--sidebar-primary-foreground);
  --color-sidebar-accent: var(--sidebar-accent);
  --color-sidebar-accent-foreground: var(--sidebar-accent-foreground);
  --color-sidebar-border: var(--sidebar-border);
  --color-sidebar-ring: var(--sidebar-ring);

  --color-warning: var(--warning);
  --color-transparent: transparent;
}

@layer base {
  * {
    @apply border-border outline-ring/50;
  }

  html,
  body {
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }

  #root {
    height: 100vh;
    width: 100vw;
    overflow: hidden;
  }

  body {
    @apply bg-background text-foreground;
  }
}

/* CUSTOM STYLES */
.talent-shadow-card {
  box-shadow: 0 21px 27px -10px var(--talent-surface-shadow);
}

.code,
pre,
code {
  font-family: var(--font-mono), serif;
  font-size: 0.95em;
  background: var(--card);
  color: var(--card-foreground);
  border-radius: 4px;
  padding: 2px 6px;
}

@keyframes loading-progress {
  0% {
    width: 30%;
  }
  50% {
    width: 80%;
  }
  100% {
    width: 70%;
  }
}
